import RoutesComponent from 'routes/data/RoutesComponent';
import NoInternetConnection from 'components/no-internet-connection/NoInternetConnection';
import { useEffect, useRef } from 'react';
import { cookieDecryption } from 'utils';
import useSocketStore from './hooks/query/messages/useSocket';
import useGetUnreadNotificationCount from './hooks/query/messages/useGetUnreadNotificationCount';
import useSocketMessage from './hooks/store/messages/useSocketStore';
import { MenteeBulkStatusChangeCompletedPayload } from 'types';
import useLoginStore from 'hooks/store/common/useLoginStore';
// @ts-ignore
import TagManager from 'react-gtm-module';
import SessionExpiredPopup from './modal/sessionExpired/SessionExpired';
import { applyNewRelicUserContext } from './observability/applyNewRelicUserContext';
import { useParams } from 'react-router';

const App = () => {
  const { isSessionExpired } = useLoginStore();
  const socketData = useSocketMessage();
  const {
    getSocket,
    socketResponse,
    handleChatMessagesMarkedAsReadEvent,
    handleMessageReactionSentEvent,
    handleMessageSentEvent,
    handleMessageStatusUpdate,
    handleMenteeBulkStatusChangeCompleted,
  }: any = useSocketStore();
  const user = cookieDecryption('user');
  const { loginResponse } = useLoginStore();
  const { getUnreadNotificationCount } = useGetUnreadNotificationCount();

  useEffect(() => {
    getSocket({ token: user?.token, userData: user });
  }, [loginResponse]);

  // IMP-2141: initial unread count comes from the REST endpoint, not the
  // 'getCounts' socket event -- that event is push-only, used to keep the
  // count live after this fetch (see IMP-717).
  //
  // Both of those write the same badge, so a slow response must not land on
  // top of a fresher value. The generation discards a response belonging to a
  // login this one has already replaced, and the snapshot discards it if
  // anything moved the count while the request was still in flight.
  const unreadCountRequest = useRef(0);

  useEffect(() => {
    if (!user?.userId) return;

    const requestGeneration = ++unreadCountRequest.current;
    const countWhenRequested = socketData.getState()?.getNotificationCount;

    getUnreadNotificationCount().then(count => {
      if (requestGeneration !== unreadCountRequest.current) return;
      if (socketData.getState()?.getNotificationCount !== countWhenRequested) return;

      if (typeof count === 'number') {
        socketData.setState({ getNotificationCount: count });
      }
    });
  }, [loginResponse]);

  const tagManagerArgs = {
    gtmId: 'GTM-PFF836CC',
  };

  useEffect(() => {
    // IMP-2245: `fetchIpAddress()` removed. It called the third-party
    // geolocation-db.com with the admin user's IP on every session, then stored the
    // result -- plus an AES copy encrypted with a key that ships in this bundle -- in
    // localStorage, for the backend to read back out of request headers. Because the
    // key is public, those values were forgeable, so the backend now takes the IP from
    // `req.ip` instead and ignores the headers entirely.
    //
    // Clear the keys we previously wrote, so a returning user's stored IP does not sit
    // in their browser indefinitely now that nothing reads it.
    try {
      localStorage.removeItem('ipAdd');
      localStorage.removeItem('ipInformation');
    } catch (e) {
      console.error(e);
    }
    TagManager.initialize(tagManagerArgs);
  }, []);

  useEffect(() => {
    if (!Object.keys(socketData.socket || {}).length) return;

    const handleMessageSent = (eventPayload: { chatId: string; messageId: string }) => {
      handleMessageSentEvent(eventPayload);
    };

    const handleMessageReactionSent = (eventPayload: {
      chatId: string;
      lastActivity: any;
      lastActivityAt: string;
      removedReaction: any;
    }) => {
      handleMessageReactionSentEvent(eventPayload);
    };

    const handleChatMessagesMarkedAsRead = (response: { success: boolean; chatId: string }) => {
      handleChatMessagesMarkedAsReadEvent(response);
    };

    const handleStatusUpdate = (eventPayload: {
      newStatus: 'Delivered' | 'Read';
      updates: { chatId: string; messageId: string }[];
    }) => {
      handleMessageStatusUpdate(eventPayload);
    };

    const handleResponse = (response: any) => {
      socketResponse({ response });
    };

    const handleMenteeBulkStatusChange = (payload: MenteeBulkStatusChangeCompletedPayload) => {
      handleMenteeBulkStatusChangeCompleted(payload);
    };

    socketData.socket.on('messageSent', handleMessageSent);
    socketData.socket.on('messageReactionSent', handleMessageReactionSent);
    socketData.socket.on('chatMessagesMarkedAsRead', handleChatMessagesMarkedAsRead);
    socketData.socket.on('messageStatusUpdate', handleStatusUpdate);
    socketData.socket.on('response', handleResponse);

    socketData.socket.on('menteeBulkStatusChangeCompleted', handleMenteeBulkStatusChange);

    return () => {
      socketData.socket.off('messageSent', handleMessageSent);
      socketData.socket.off('messageReactionSent', handleMessageReactionSent);
      socketData.socket.off('chatMessagesMarkedAsRead', handleChatMessagesMarkedAsRead);
      socketData.socket.off('messageStatusUpdate', handleStatusUpdate);
      socketData.socket.off('response', handleResponse);

      socketData.socket.off('menteeBulkStatusChangeCompleted', handleMenteeBulkStatusChange);
    };
  }, [socketData.socket]);

  useEffect(() => {
    if (Object?.keys(user || {})?.length > 0) {
      const tagManagerArgs = {
        gtmId: 'GTM-PFF836CC',
        dataLayer: {
          user_type: user?.role?.toLowerCase(),
          user_org: user?.partnerName || user?.region,
          user_school:
            user?.role === 'Super Admin' ? '' : user?.school?.length > 0 ? user?.school?.join(',')?.toLowerCase() : '',
        },
      };
      TagManager.dataLayer(tagManagerArgs);
    }
  }, [loginResponse]);

  useEffect(() => {
    applyNewRelicUserContext(loginResponse);
  }, [loginResponse]);

  window.addEventListener('storage', function (e: StorageEvent) {
    if (e.key === 'userStatus') {
      window.location.reload();
    }
  });

  return (
    <>
      {isSessionExpired && <SessionExpiredPopup />}
      <NoInternetConnection>
        <RoutesComponent />
      </NoInternetConnection>
    </>
  );
};

export default App;
